import { Injectable, inject } from '@angular/core';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { getAuthTokenFromCookie, isJwtExpired } from '@neworg-net/amsuite-core/auth';
import { MsalService } from '@azure/msal-angular';

import { AppService } from './app.service';
import { SessionExpiredService } from './session-expired.service';

/**
 * LegacySessionService (host-only)
 *
 * Gestisce il rilevamento della scadenza della sessione ASP legacy (.AspNet.Cookies)
 * del monolite, indipendentemente dalla sessione moderna Bearer/authToken.
 *
 * - Usa probe attivo su endpoint legacy esistenti (/suite/getdataowner) che richiedono il cookie ASP.
 * - Distingue il caso "moderno ancora valido" vs sessione moderna scaduta.
 * - Riutilizza esattamente lo stesso banner/overlay del flusso Bearer (`SessionExpiredService`).
 * - Quando mostriamo il banner: solo show(), stop. Come prima.
 * - Il bootstrap del cookie legacy (bootstrapBackendCookie) rimane **solo** al login iniziale dopo MSAL.
 *
 * Non tocca il legacy. Throttled per performance.
 */
@Injectable({ providedIn: 'root' })
export class LegacySessionService {
  private static readonly THROTTLE_MS = 5 * 60 * 1000; // 5 minuti

  private readonly appService = inject(AppService);
  private readonly config = inject(ConfigService);
  private readonly sessionExpired = inject(SessionExpiredService);
  private readonly msalService = inject(MsalService);

  private lastProbeTs = 0;
  private lastValid = true;
  private probeInFlight: Promise<boolean> | null = null;

  /**
   * Probe throttled + ensure.
   * Ritorna true se la sessione legacy è valida.
   * Se scaduta (e moderno OK) mostra il banner "Sessione scaduta" identico al caso moderno.
   * Niente redirect al vecchio login qui: l'utente va al login nuovo dal banner.
   */
  probeAndEnsureValid(): Promise<boolean> {
    return this.runProbe(false);
  }

  /**
   * Probe richiesto da una notifica iframe: bypassa il throttle, ma condivide un
   * eventuale probe gia' in corso per evitare richieste e banner duplicati.
   */
  forceProbeAndEnsureValid(): Promise<boolean> {
    return this.runProbe(true);
  }

  private runProbe(force: boolean): Promise<boolean> {
    if (this.probeInFlight) {
      return this.probeInFlight;
    }
    if (!force && Date.now() - this.lastProbeTs < LegacySessionService.THROTTLE_MS) {
      return Promise.resolve(this.lastValid);
    }

    this.lastProbeTs = Date.now();
    const probe = this.executeProbe();
    const trackedProbe = probe.then(
      result => {
        if (this.probeInFlight === trackedProbe) {
          this.probeInFlight = null;
        }
        return result;
      },
      error => {
        if (this.probeInFlight === trackedProbe) {
          this.probeInFlight = null;
        }
        throw error;
      },
    );
    this.probeInFlight = trackedProbe;
    return trackedProbe;
  }

  private async executeProbe(): Promise<boolean> {
    try {
      await this.appService.getDataOwner();
      this.lastValid = true;
      return true;
    } catch (err: unknown) {
      if (this.isModernSessionValid() && this.isLegacyAuthError(err)) {
        this.lastValid = false;
        await this.showExpiredBanner();
        return false;
      }
      // Errore non legato a legacy auth (network, 500, ecc.) — propaga
      throw err;
    }
  }

  /** True se la sessione moderna (authToken JWT o account MSAL) è ancora valida. */
  isModernSessionValid(): boolean {
    try {
      const key = this.config.cryptingKey;
      const token = getAuthTokenFromCookie(key);
      if (token && !isJwtExpired(token)) {
        return true;
      }
    } catch {
      // fallback sotto
    }
    // Fallback: presenza di account MSAL (modern auth attiva)
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  private isLegacyAuthError(err: unknown): boolean {
    const status = (err as { status?: unknown } | null)?.status;
    return status === 401 || status === 403;
  }

  /** Esposto per check rapidi da chi chiamante (senza ri-probe). */
  public isAuthErrorForLegacy(err: unknown): boolean {
    return this.isLegacyAuthError(err);
  }

  private async showExpiredBanner(): Promise<void> {
    // Esattamente come per il caso Bearer/moderno:
    // mostriamo SOLO il banner "Sessione scaduta" e stop.
    // Niente redirect "strano" al vecchio /auth/login.
    // L'utente clicca la CTA del banner e va al login NUOVO (come prima).
    await this.sessionExpired.show(4);
  }
}
